import { faCircleDollar, faHandHoldingDollar } from '@fortawesome/pro-regular-svg-icons';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { getAccrualReportThunk, getCashReportThunk } from '@spike/payments-action';
import clsx from 'clsx';
import {
    AccrualReport,
    CashReport,
    CashReportDetailCategory,
    PaymentBreakdown,
    PaymentsSalesReports
} from '@spike/payments-model';
import moment from 'moment-timezone';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PaymentsStatus } from '@spike/payments-action';
import { RootState } from 'store';
import Header from './Header';
import { ReportItem, ReportItemType } from './SalesReportsModel';
import Section from './Section';
import Summary from './Summary';
import { Period } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import { formatSalesReportItemValue, salesReportItemvalueType } from 'utils/PaymentsUtils';
import CashSummary from './CashSummary';
import CashReportDetail from './CashReportDetail';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface SalesReportsProps {
    className?: string;
    showTitle?: boolean; // si false, oculta el título "Sales Reports"
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            height: 'fit-content',
            padding: '24px 0px',

            [theme.breakpoints.up('md')]: {
                padding: '50px 24px'
            }
        },
        title: {
            color: '#222',
            fontSize: 20,
            lineHeight: 1,
            fontWeight: 600,

            [theme.breakpoints.down('sm')]: {
                padding: '0px 16px'
            },

            [theme.breakpoints.up('md')]: {
                fontSize: '24px'
            }
        },
        mobileWrapper: {
            [theme.breakpoints.down('sm')]: {
                marginTop: 24,
                padding: '24px 16px 0px',
                backgroundColor: '#FAFAFA'
            }
        },
        summary: {
            marginTop: 24,

            [theme.breakpoints.up('md')]: {
                marginTop: '34px'
            }
        },
        section: {
            marginTop: 24,

            [theme.breakpoints.up('md')]: {
                marginTop: '37px'
            }
        }
    })
);

// The API may return an empty array instead of an object when there is no breakdown
const sumPaymentBreakdown = (breakdown: PaymentBreakdown | undefined): number => {
    if (!breakdown || Array.isArray(breakdown)) {
        return 0;
    }

    return (
        (breakdown.cash ?? 0) +
        (breakdown.creditCard ?? 0) +
        (breakdown.pos ?? 0) +
        (breakdown.payByLink ?? 0) +
        (breakdown.other ?? 0)
    );
};

export const SalesReports: FunctionComponent<SalesReportsProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const cashReport = useSelector<RootState, CashReport | undefined>(state => state.payments.cashReport);
    const accrualReport = useSelector<RootState, AccrualReport | undefined>(state => state.payments.accrualReport);
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [loading, setLoading] = useState(true);
    const [reportType, setReportType] = useState<'cash' | 'accrual'>('cash');

    const [marketplaceIds, setMarketplaceIds] = useState(organizationMarketplaces.map(m => m.id));
    const [period, setPeriod] = useState<Period | null>({ from: moment(), to: moment() });
    const [detailCategory, setDetailCategory] = useState<CashReportDetailCategory | null>(null);

    const openDetail = (category: CashReportDetailCategory) => () => setDetailCategory(category);

    let salesItems: Array<ReportItem> = [
        {
            key: '1',
            label: 'Services',
            value: formatSalesReportItemValue(cashReport?.totals?.serviceAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL,
            onClick: openDetail('service')
        },
        {
            key: '2',
            label: 'Add-On Services',
            value: formatSalesReportItemValue(cashReport?.totals?.addonAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL,
            onClick: openDetail('addon')
        },
        {
            key: '3',
            label: 'Memberships',
            value: formatSalesReportItemValue(cashReport?.totals?.membershipAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL,
            onClick: openDetail('membership')
        },
        {
            key: '4',
            label: 'Products',
            value: formatSalesReportItemValue(cashReport?.totals?.productAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL,
            onClick: openDetail('product')
        },
        {
            key: '5',
            label: 'Custom Items',
            value: formatSalesReportItemValue(cashReport?.totals?.customAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL,
            onClick: openDetail('custom')
        },
        {
            key: '6',
            label: 'Taxes',
            value: formatSalesReportItemValue(cashReport?.totals?.taxAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL
        },
        {
            key: '7',
            label: 'Surcharges Collected',
            value: formatSalesReportItemValue(cashReport?.totals?.surchargeAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL,
            info: 'Surcharges collected from card payments, minus surcharge amounts returned in refunds.'
        },
        {
            key: '8',
            label: 'Tips',
            value: formatSalesReportItemValue(cashReport?.totals?.tipAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL
        },
        {
            key: '9',
            label: 'Cuddles Fees',
            value: formatSalesReportItemValue(cashReport?.totals?.cuddlespayFee, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL
        },
        {
            key: '10',
            label: 'Payouts',
            value: formatSalesReportItemValue(cashReport?.totals?.payoutAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL
        },
        {
            key: '11',
            label: 'Refunds',
            value: formatSalesReportItemValue(cashReport?.totals?.refundAmount, salesReportItemvalueType.Money),
            type: ReportItemType.NORMAL,
            onClick: openDetail('refund')
        }
    ];

    if (reportType === 'accrual') {
        salesItems = [
            {
                key: '1',
                label: 'Enrollment',
                value: '',
                type: ReportItemType.STRONG
            },
            {
                key: '2',
                label: 'Total Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales?.enrollment?.totalRecognized || 0,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '3',
                label: 'Total Discount Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales?.enrollment?.totalDiscountRecognized || 0,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '4',
                label: 'Total Deferred',
                value: formatSalesReportItemValue(
                    accrualReport?.sales?.enrollment?.totalDeferred || 0,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '4',
                label: 'Total Deferred Discount',
                value: formatSalesReportItemValue(
                    accrualReport?.sales?.enrollment?.totalDeferredDiscount || 0,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '5',
                label: 'Services',
                value: '',
                type: ReportItemType.STRONG
            },
            {
                key: '6',
                label: 'Total Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.service.totalRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '7',
                label: 'Total Discount Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.service.totalDiscountRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '8',
                label: 'Total Deferred',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.service.totalDeferred,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '9',
                label: 'Total Deferred Discount',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.service.totalDeferredDiscount,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '10',
                label: 'Add-On Services',
                value: '',
                type: ReportItemType.STRONG
            },
            {
                key: '11',
                label: 'Total Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.addonService.totalRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '12',
                label: 'Total Discount Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.addonService.totalDiscountRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '13',
                label: 'Total Deferred',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.addonService.totalDeferred,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '14',
                label: 'Total Deferred Discount',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.addonService.totalDeferredDiscount,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '15',
                label: 'Products',
                value: '',
                type: ReportItemType.STRONG
            },
            {
                key: '16',
                label: 'Total Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.businessProduct.totalRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '17',
                label: 'Total Discount Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.businessProduct.totalDiscountRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '18',
                label: 'Total Deferred',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.businessProduct.totalDeferred,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '19',
                label: 'Total Deferred Discount',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.businessProduct.totalDeferredDiscount,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '20',
                label: 'Custom Items',
                value: '',
                type: ReportItemType.STRONG
            },
            {
                key: '21',
                label: 'Total Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.customItem.totalRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '22',
                label: 'Total Discount Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.customItem.totalDiscountRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '23',
                label: 'Total Deferred',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.customItem.totalDeferred,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '24',
                label: 'Total Deferred Discount',
                value: formatSalesReportItemValue(
                    accrualReport?.sales.customItem.totalDeferredDiscount,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '25',
                label: 'Collected Tax',
                value: formatSalesReportItemValue(accrualReport?.collectedTax, salesReportItemvalueType.Money),
                type: ReportItemType.NORMAL
            },
            {
                key: '26',
                label: 'Totals',
                value: '',
                type: ReportItemType.STRONG
            },
            {
                key: '27',
                label: 'Recognized',
                value: formatSalesReportItemValue(accrualReport?.totals.recognized, salesReportItemvalueType.Money),
                type: ReportItemType.SUBITEM
            },
            {
                key: '28',
                label: 'Discount Recognized',
                value: formatSalesReportItemValue(
                    accrualReport?.totals.discountRecognized,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            },
            {
                key: '29',
                label: 'Deferred',
                value: formatSalesReportItemValue(accrualReport?.totals.deferred, salesReportItemvalueType.Money),
                type: ReportItemType.SUBITEM
            },
            {
                key: '30',
                label: 'Deferred Discount',
                value: formatSalesReportItemValue(
                    accrualReport?.totals.deferredDiscount,
                    salesReportItemvalueType.Money
                ),
                type: ReportItemType.SUBITEM
            }
        ];
    }

    const netTotal = sumPaymentBreakdown(cashReport?.payments) - sumPaymentBreakdown(cashReport?.refunds);

    const totalItems: Array<ReportItem> = [
        {
            key: '1',
            label: 'Total Payments',
            value: '',
            type: ReportItemType.STRONG
        },
        {
            key: '2',
            label: 'Cash',
            value: formatSalesReportItemValue(
                Array.isArray(cashReport?.payments) ? 0 : (cashReport?.payments as any)?.cash,
                salesReportItemvalueType.Money
            ),
            type: ReportItemType.SUBITEM
        },
        {
            key: '3',
            label: 'Credit Cards',
            value: formatSalesReportItemValue(cashReport?.payments?.creditCard, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '4',
            label: 'POS',
            value: formatSalesReportItemValue(cashReport?.payments?.pos, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '5',
            label: 'Pay By Link',
            value: formatSalesReportItemValue(cashReport?.payments?.payByLink, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '6',
            label: 'Other',
            value: formatSalesReportItemValue(cashReport?.payments?.other, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '7',
            label: 'Total Refunds',
            value: '',
            type: ReportItemType.STRONG
        },
        {
            key: '8',
            label: 'Cash',
            value: formatSalesReportItemValue(cashReport?.refunds?.cash, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '9',
            label: 'Credit Cards',
            value: formatSalesReportItemValue(cashReport?.refunds?.creditCard, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '10',
            label: 'POS',
            value: formatSalesReportItemValue(cashReport?.refunds?.pos, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '11',
            label: 'Pay By Link',
            value: formatSalesReportItemValue(cashReport?.refunds?.payByLink, salesReportItemvalueType.Money),
            type: ReportItemType.SUBITEM
        },
        {
            key: '12',
            label: 'Other',
            value: formatSalesReportItemValue(
                Array.isArray(cashReport?.refunds) ? 0 : (cashReport?.refunds as any)?.other,
                salesReportItemvalueType.Money
            ),
            type: ReportItemType.SUBITEM
        },
        {
            key: '13',
            label: 'Net Total',
            value: formatSalesReportItemValue(netTotal, salesReportItemvalueType.Money),
            type: ReportItemType.TOTAL
        }
    ];

    useEffect(() => {
        dispatch(getCashReportThunk(apiClientWrapper, moment(), moment(), marketplaceIds));
    }, []);

    // Helper function to dispatch API calls
    const dispatchReportCall = (
        reportType: 'cash' | 'accrual',
        period: Period | null,
        marketplaceIds: Array<number>
    ) => {
        const thunk = reportType === 'cash' ? getCashReportThunk : getAccrualReportThunk;
        dispatch(thunk(apiClientWrapper, period?.from || null, period?.to || null, marketplaceIds));
    };

    // Handle loading state
    useNonInitialEffect(() => {
        const successStatuses = [
            PaymentsStatus.GetCashReportSuccess,
            PaymentsStatus.GetAccrualReportSuccess,
            PaymentsStatus.Error
        ];

        if (successStatuses.includes(paymentsStatus)) {
            setLoading(false);
        }
    }, [paymentsStatus]);

    // Event handlers
    const changePeriodHandler = (period: Period | null) => {
        setLoading(true);
        setPeriod(period);
        dispatchReportCall(reportType, period, marketplaceIds);
    };

    const handleReportTypeChange = (newReportType: 'cash' | 'accrual', period: Period | null) => {
        setLoading(true);
        setDetailCategory(null);
        setReportType(newReportType);
        dispatchReportCall(newReportType, period, marketplaceIds);
    };

    const handleMarketplaceIdsChange = (newMarketplaceIds: Array<number>, period: Period | null) => {
        setMarketplaceIds(newMarketplaceIds);
        setPeriod(period);
        setLoading(true);
        dispatchReportCall(reportType, period, newMarketplaceIds);
    };

    const { showTitle = true } = props;

    return (
        <Box className={clsx(classes.container, props.className)}>
            {showTitle && <Typography className={classes.title}>Sales Reports</Typography>}

            <Box className={classes.mobileWrapper}>
                <Header
                    cashReport={cashReport}
                    accrualReport={accrualReport}
                    reportType={reportType}
                    selectedMarketplaceIds={marketplaceIds}
                    hideExport={detailCategory !== null}
                    onChangePeriod={changePeriodHandler}
                    onReportTypeChange={handleReportTypeChange}
                    onChangeMarketplaceIds={handleMarketplaceIdsChange}
                />
                {reportType === 'cash' && detailCategory ? (
                    <CashReportDetail
                        category={detailCategory}
                        from={period?.from ?? null}
                        to={period?.to ?? null}
                        marketplaceIds={marketplaceIds}
                        onBack={() => setDetailCategory(null)}
                    />
                ) : (
                    <>
                        {reportType === 'cash' && (
                            <CashSummary
                                cashReport={cashReport}
                                className={classes.summary}
                                loading={loading}
                            />
                        )}
                        <Section
                            icon={faCircleDollar}
                            title={reportType === 'cash' ? 'Cash' : 'Accrual'}
                            items={salesItems}
                            className={classes.section}
                            loading={loading}
                        />
                        {reportType === 'cash' && (
                            <Section
                                icon={faHandHoldingDollar}
                                title="Total"
                                items={totalItems}
                                className={classes.section}
                                loading={loading}
                            />
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default SalesReports;
